'use client';

import React from 'react';
import { Table as ChakraTable } from '@chakra-ui/react';

/**
 * Table components — thin wrappers around Chakra UI v3 Table primitives.
 *
 * Previously implemented as `Box as="table/tr/td"`, which silently dropped
 * every style prop (width, textAlign, padding, backgroundColor, …) through
 * the native-element prop filter — tables could not be styled at all and
 * never stretched to their container. Wrapping the real Chakra Table keeps
 * the public API (TableRoot/TableHeader/…) while making style props work.
 */

type ChakraTableRootProps = React.ComponentProps<typeof ChakraTable.Root>;

export interface TableProps extends Omit<ChakraTableRootProps, 'variant'> {
  /** Table variant ('simple' and 'striped' map to Chakra's 'line' variant) */
  variant?: 'simple' | 'striped' | 'outline' | 'line';
  /** Table size */
  size?: 'sm' | 'md' | 'lg';
}

export const Table: React.FC<TableProps> = ({
  variant = 'simple',
  size = 'md',
  striped,
  children,
  ...rootProps
}) => {
  const chakraVariant = variant === 'outline' ? 'outline' : 'line';

  return (
    <ChakraTable.Root
      variant={chakraVariant}
      size={size}
      striped={striped ?? variant === 'striped'}
      width="100%"
      {...rootProps}
    >
      {children}
    </ChakraTable.Root>
  );
};

export type TableHeaderProps = React.ComponentProps<typeof ChakraTable.Header>;

export const TableHeader: React.FC<TableHeaderProps> = ({ children, ...props }) => {
  return <ChakraTable.Header {...props}>{children}</ChakraTable.Header>;
};

export type TableBodyProps = React.ComponentProps<typeof ChakraTable.Body>;

export const TableBody: React.FC<TableBodyProps> = ({ children, ...props }) => {
  return <ChakraTable.Body {...props}>{children}</ChakraTable.Body>;
};

export interface TableRowProps extends React.ComponentProps<typeof ChakraTable.Row> {
  /** Whether the row is selected */
  isSelected?: boolean;
}

export const TableRow: React.FC<TableRowProps> = ({
  isSelected = false,
  children,
  ...props
}) => {
  return (
    <ChakraTable.Row
      backgroundColor={isSelected ? 'bg.muted' : undefined}
      _hover={{ backgroundColor: 'bg.muted' }}
      {...props}
    >
      {children}
    </ChakraTable.Row>
  );
};

export interface TableCellProps extends React.ComponentProps<typeof ChakraTable.Cell> {
  /** Whether this is a header cell (renders th) */
  isHeader?: boolean;
  /** Text alignment */
  align?: 'left' | 'center' | 'right';
}

export const TableCell: React.FC<TableCellProps> = ({
  isHeader = false,
  align = 'left',
  children,
  ...props
}) => {
  const Component = isHeader ? ChakraTable.ColumnHeader : ChakraTable.Cell;

  return (
    <Component textAlign={align} {...props}>
      {children}
    </Component>
  );
};

export type TableColumnHeaderProps = React.ComponentProps<typeof ChakraTable.ColumnHeader>;

export const TableColumnHeader: React.FC<TableColumnHeaderProps> = ({ children, ...props }) => {
  return <ChakraTable.ColumnHeader {...props}>{children}</ChakraTable.ColumnHeader>;
};

/** @deprecated Use TableColumnHeader for header cells or TableCell for data cells */
export const TableHead = TableCell;

export interface TableCaptionProps extends React.ComponentProps<typeof ChakraTable.Caption> {
  /** Caption placement */
  placement?: 'top' | 'bottom';
}

export const TableCaption: React.FC<TableCaptionProps> = ({
  placement = 'bottom',
  children,
  ...props
}) => {
  return (
    <ChakraTable.Caption captionSide={placement} padding={4} color="fg.muted" fontSize="sm" {...props}>
      {children}
    </ChakraTable.Caption>
  );
};

// Aliases for Chakra UI v3 compatibility
export const TableRoot = Table;

export default Table;
